export interface ExportColumn<T = any> {
    header: string;
    field?: string;
    value?: (row: T) => any;
}

const CSV_SEPARATOR = ';';

const needsQuoting = (str: string): boolean => /[;"\r\n]/.test(str);

const escapeCsvField = (value: any): string => {
    const str = value === null || value === undefined ? '' : String(value);
    if (!needsQuoting(str)) return str;
    return '"' + str.replace(/"/g, '""') + '"';
};

export const rowsToCsv = <T,>(columns: ExportColumn<T>[], rows: T[]): string => {
    const cellValue = (col: ExportColumn<T>, row: T) => col.value ? col.value(row) : (row as any)[col.field];
    const header = columns.map(col => escapeCsvField(col.header)).join(CSV_SEPARATOR);
    const body = rows.map(row => columns.map(col => escapeCsvField(cellValue(col, row))).join(CSV_SEPARATOR)).join('\n');
    const bom = String.fromCharCode(0xfeff); // so that Excel opens the file as UTF-8
    return bom + header + '\n' + body;
};

export const downloadBlob = (filename: string, blob: Blob): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename: string, csv: string): void => {
    const blob = new Blob([csv], { type: 'application/csv;charset=utf-8;' });
    downloadBlob(filename.endsWith('.csv') ? filename : `${filename}.csv`, blob);
};

export const exportRowsToCsv = <T,>(filename: string, columns: ExportColumn<T>[], rows: T[]): void => {
    downloadCsv(filename, rowsToCsv(columns, rows));
};

export const filenameTimestamp = (): string => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
};

export const withTimestamp = (base: string): string => `${base}_${filenameTimestamp()}`;

export const escapeDataTableCsvValue = (value: any): string => String(value ?? '').replace(/"/g, '""').replace(/\n/g, '\u2028');

export const excelTextDataTableCsvValue = (value: any): string => value ? `=""${escapeDataTableCsvValue(value).replace(/"/g, '""')}""` : '';

export const exportDataTableCsv = (dt: { current: any }, baseName: string): void => {
    if (!dt.current) return;
    dt.current.props.exportFilename = withTimestamp(baseName);
    dt.current.props.csvSeparator = CSV_SEPARATOR;
    dt.current.exportCSV();
};
